/**
 * #1049 — Call Sheet Notify: WHO gets notified, decided in one place.
 *
 * ed, 2024-05-14: "When clicking on a cell phone for the notify to that day,
 * group, or individual, a pop up needs to display with instruction. Select
 * other groups or individuals to notify. CANCEL/SEND. If the master notify
 * icon for the day is selected then all other groups are unselected."
 *
 * ed, 2026-09-30: "Before the Whole-day box, offer a status filter: Confirmed
 * and/or Unconfirmed, as two checkboxes. Either one or both can be ticked, and
 * recipients are the selected calls whose status matches." (Replaces the
 * 2026-09-29 "confirmed only" ruling.) Declined and unassigned calls are never
 * notified; neither is a call whose employee has been deleted.
 *
 * Pure functions, no Vue, no network: the popup renders what these return and
 * the send posts what sendPlan() describes. Nothing here sends anything.
 *
 * ── the two views hand over different rows ──
 *   bulk        a row is a GROUP: { call: [singleCall, …], skills: […], start, end }
 *   individual  a row is ONE CALL: { id, start, end, skill, employee, status }
 * Both become the same ITEM shape, so the popup and the rules do not care which
 * view opened them.
 *
 * ── the status filter ──
 *   { confirmed: bool, unconfirmed: bool } — which statuses the selection reaches.
 * A call "matches" when its status is ticked AND there is somebody to tell.
 */

import { findContentProblems, describeContentProblems } from './contentRules.js';

export const CONFIRMED = "Confirmed";
export const UNCONFIRMED = "Unconfirmed";
export const MAX_MESSAGE_LENGTH = 160;          // the phone sheet's limit, unchanged

const str = (v) => (v === null || v === undefined ? "" : String(v));

/** What is ticked when the popup opens: both, so the icon that was clicked reaches its own row. */
export function defaultStatuses() {
  return { confirmed: true, unconfirmed: true };
}

function personName(employee) {
  const p = employee && employee.employable;
  if (!p) return "";
  return (str(p.first_name) + " " + str(p.last_name)).trim();
}

/** One call, reduced to what the rules need. */
function reduceCall(call) {
  const employee = call && call.employee;
  const status = call && call.status && call.status.name ? str(call.status.name) : "";
  const assigned = !!(employee && employee.id);
  // Somebody to tell. A deleted employee is nobody to tell.
  const reachable = assigned && !employee.deleted_at;
  return {
    id: str(call && call.id),
    employeeId: assigned ? str(employee.id) : "",
    name: assigned ? personName(employee) : "",
    status,
    assigned,
    confirmed: reachable && status === CONFIRMED,
    unconfirmed: reachable && status === UNCONFIRMED,
    start: str(call && call.start),
    end: str(call && call.end),
  };
}

/** Does this call match the ticked statuses? */
export function matches(call, statuses) {
  const s = statuses || {};
  return !!((s.confirmed && call.confirmed) || (s.unconfirmed && call.unconfirmed));
}

function skillLabel(skill) {
  if (!skill) return "";
  const pr = skill.priotiy !== undefined && skill.priotiy !== null ? "(" + skill.priotiy + ")" : "";   // sic — the API's spelling
  return str(skill.name) + pr;
}

/**
 * The items of one day. Status-agnostic: the counts are per status, and
 * whether an item can be ticked is `isSelectable(item, statuses)`.
 *
 * @param {Array}  rows  the day's rows exactly as the view holds them
 * @param {string} view  "bulk" | "individual"
 * @return {Array<{key:string, position:string, start:string, end:string,
 *                 calls:Array, confirmed:number, unconfirmed:number,
 *                 assigned:number, total:number}>}
 */
export function itemsForDay(rows, view) {
  const out = [];
  (rows || []).forEach((row, index) => {
    if (!row) return;
    let calls;
    let position;
    let start;
    let end;
    if (view === "bulk") {
      calls = (row.call || []).filter(Boolean).map(reduceCall);
      position = (row.skills || []).map(skillLabel).filter(Boolean).join(", ");
      start = str(row.start || (calls[0] && calls[0].start));
      end = str(row.end || (calls[0] && calls[0].end));
    } else {
      calls = [reduceCall(row)];
      position = skillLabel(row.skill);
      start = str(row.start);
      end = str(row.end);
    }
    calls = calls.filter((c) => c.id !== "");
    out.push({
      key: view + ":" + index + ":" + (calls[0] ? calls[0].id : "empty"),
      position,
      start,
      end,
      calls,
      confirmed: calls.filter((c) => c.confirmed).length,
      unconfirmed: calls.filter((c) => c.unconfirmed).length,
      assigned: calls.filter((c) => c.assigned).length,
      total: calls.length,
    });
  });
  return out;
}

/** Nobody matching the ticked statuses = nobody this item could notify, so it cannot be ticked. */
export function isSelectable(item, statuses) {
  return !!item && item.calls.some((c) => matches(c, statuses));
}

/** A selection: the whole day, or a set of item keys — never both. */
export function emptySelection() {
  return { wholeDay: false, keys: [] };
}

/**
 * What is ticked when the popup opens.
 *   day icon        → the whole day, and no group ("all other groups are unselected")
 *   group/call icon → that one item, if it reaches anybody under the ticked statuses
 */
export function initialSelection(items, clicked, statuses) {
  if (clicked && clicked.type === "day") {
    return { wholeDay: true, keys: [] };
  }
  const item = (items || []).find((i) => clicked && i.key === clicked.key);
  return { wholeDay: false, keys: isSelectable(item, statuses) ? [item.key] : [] };
}

/** Ticking the day clears every group; unticking it leaves nothing selected. */
export function toggleWholeDay(selection, on) {
  return { wholeDay: !!on, keys: [] };
}

/** Ticking any group unticks the day. An item that reaches nobody cannot be ticked. */
export function toggleItem(items, selection, key, on, statuses) {
  const item = (items || []).find((i) => i.key === key);
  const keys = (selection.keys || []).filter((k) => k !== key);
  if (on && isSelectable(item, statuses)) keys.push(key);
  return { wholeDay: false, keys };
}

/**
 * Ticking or unticking a status. The whole-day tick survives; a ticked group
 * that no longer reaches anybody is dropped, so what is ticked is always sendable.
 * @return {{statuses:Object, selection:Object}}
 */
export function toggleStatus(items, selection, statuses, which, on) {
  const next = Object.assign({}, statuses || {});
  next[which] = !!on;
  const keys = (selection.keys || []).filter((k) => isSelectable((items || []).find((i) => i.key === k), next));
  return { statuses: next, selection: { wholeDay: !!selection.wholeDay, keys } };
}

/**
 * The matching calls the selection reaches, one per PERSON.
 *
 * The send endpoints message once per call, so somebody on two calls in the
 * selection would get the same text twice. One message per person: the first
 * of their calls (by start) carries it, the rest are counted.
 *
 * @return {{calls:Array, callIds:string[], people:number, matchedCalls:number,
 *           duplicatesCollapsed:number, otherStatus:number, unassigned:number}}
 */
export function recipientsOf(items, selection, statuses) {
  const chosen = (items || []).filter((i) =>
    selection && (selection.wholeDay || (selection.keys || []).indexOf(i.key) >= 0));
  const all = [];
  chosen.forEach((i) => i.calls.forEach((c) => all.push(c)));

  const matched = all.filter((c) => matches(c, statuses))
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  const seen = {};
  const calls = [];
  matched.forEach((c) => {
    if (seen[c.employeeId]) return;
    seen[c.employeeId] = true;
    calls.push(c);
  });

  return {
    calls,
    callIds: calls.map((c) => c.id),
    people: calls.length,
    matchedCalls: matched.length,
    duplicatesCollapsed: matched.length - calls.length,
    otherStatus: all.filter((c) => c.assigned && !matches(c, statuses)).length,
    unassigned: all.filter((c) => !c.assigned).length,
  };
}

/**
 * What the send WILL post — the phone sheet's endpoints, no new send path.
 * No call info: the popup has no such option (ed, 2026-09-30).
 * Returns null when there is nobody to notify. This describes; it does not send.
 */
export function sendPlan(recipients, message, userId) {
  const ids = (recipients && recipients.callIds) || [];
  if (!ids.length) return null;
  const common = { message: str(message), call_info: "no", user_id: str(userId) };
  if (ids.length === 1) {
    return { endpoint: "job/individualNotify", body: Object.assign({ call_id: ids[0] }, common) };
  }
  // selectedNotify reads `json_decode($request->call_ids)` — a JSON string, not an array.
  return { endpoint: "job/selectedNotify", body: Object.assign({ call_ids: JSON.stringify(ids) }, common) };
}

/**
 * The phone sheet's message checks, so the same text is refused in both places.
 * Server-side limits are #2005's.
 *
 * #1952 — this carried its OWN copy of the four patterns, an eleventh alongside
 * the ten in the components. Its docblock promised parity with
 * phoneSheet.vue; keeping that promise means sharing the rules rather than
 * restating them, or this becomes the one notify box that still rejects
 * indentation. Now delegates to src/contentRules.js and names the offender.
 */
export function messageProblem(message) {
  const text = str(message);
  if (text.trim() === "") return "You cannot send a blank message";
  if (text.length > MAX_MESSAGE_LENGTH) return "The message is longer than " + MAX_MESSAGE_LENGTH + " characters";

  return describeContentProblems(findContentProblems(text));
}
